import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'connected-react-router'
import { common, layouts } from '@ocean/ui'
import { SessionUtils, ToastUtils } from '@ocean/utils'
import { EnvironmentUtils, UserUtils } from 'utils'
import { handleUnauthorizedEvent } from 'utils/EventUtils'
import { useLazySelector } from 'hooks'
import { redirectToSta } from 'core/staIntegration/slices/staIntegration'
import { FullScreenIframe, Skeleton } from 'components/common'
import { SkeletonVariant } from 'components/common/Skeleton'
import simulatorTrainingInstructorILSimulatorListRoutes from 'modules/simulatorTrainings/instructor/instructorLed/SimulatorList/routing/routes'

const { PageLayout, PageSegment } = layouts
const { Decorator } = common

const AssessmentPlatform: React.FC = () => {
  const assessmentPlatformRef = useRef<HTMLIFrameElement>(null)
  const { isLoading, error, customerId } = useLazySelector(({ system }) => {
    const { isLoading, result, error } = system.customerInformation
    return {
      isLoading,
      customerId: result?.information.customerId,
      error,
    }
  })
  const dispatch = useDispatch()
  const { simulatorDashboardForAdministratorsAvailable } =
    EnvironmentUtils.getPermissions('simulatorTraining')

  useEffect(() => {
    const exitAssessmentPlatform = (e: MessageEvent) => {
      if (e.data.type === 'EXIT_OAP') {
        if (simulatorDashboardForAdministratorsAvailable) {
          dispatch(push(simulatorTrainingInstructorILSimulatorListRoutes.root))
        } else {
          dispatch(redirectToSta())
        }
      }
    }
    const cleanupUnauthorizedEvent = handleUnauthorizedEvent(dispatch)
    window.addEventListener('message', exitAssessmentPlatform)
    return () => {
      window.removeEventListener('message', exitAssessmentPlatform)
      cleanupUnauthorizedEvent()
    }
  }, [dispatch, simulatorDashboardForAdministratorsAvailable])

  const sendTokenAndUserInfo = () => {
    if (assessmentPlatformRef.current) {
      const sessionToken = SessionUtils.getSessionToken()
      const { connectedPerson, login } = UserUtils.getUserAccountInfo()
      assessmentPlatformRef.current.contentWindow?.postMessage(
        {
          type: 'OAP',
          sessionToken,
          connectedPerson,
          username: login,
          customerId,
        },
        window.cc.assessmentPlatform ?? ''
      )

      const trackVerticalElements = document.querySelectorAll<HTMLElement>(
        '[data-selector="TrackVertical"]'
      )

      if (trackVerticalElements.length >= 2) {
        for (let i = trackVerticalElements.length - 1; i >= trackVerticalElements.length - 2; i--) {
          trackVerticalElements[i].style.visibility = 'hidden'
        }
      }
      const parentDiv = assessmentPlatformRef.current.parentElement
      if (parentDiv) {
        parentDiv.style.overflow = 'hidden'
      }
    }
  }

  if (isLoading) {
    return <Skeleton variant={SkeletonVariant.Details} />
  }

  if (error) {
    ToastUtils.error(error.error)
    if (simulatorDashboardForAdministratorsAvailable) {
      dispatch(push(simulatorTrainingInstructorILSimulatorListRoutes.root))
    } else {
      dispatch(redirectToSta())
    }
    return null
  }

  return (
    <PageLayout>
      <PageSegment segmentId='content'>
        <Decorator root backgroundColor='white' textColor='black'>
          <FullScreenIframe
            title='Assessment Platform'
            src={`${window.cc.assessmentPlatform}`}
            ref={assessmentPlatformRef}
            onLoad={sendTokenAndUserInfo}
            style={{ display: 'flex', border: 'none', width: '100%', height: '100%' }}
          />
        </Decorator>
      </PageSegment>
    </PageLayout>
  )
}

export default AssessmentPlatform
